import { ButtonBase, Grid, createStyles, makeStyles } from '@material-ui/core';
import MailOutlinedIcon from '@material-ui/icons/MailOutlined';
import PhoneInTalkOutlinedIcon from '@material-ui/icons/PhoneInTalkOutlined';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import {
  getLoggedEmail,
  getLoggedRole,
} from 'data-layer-cr/dist/authentication';
import { Quotation } from 'data-layer-cr/dist/models';
import { ConfirmationDialog, colors } from 'design-system';
import { useCallback, useEffect, useState } from 'react';
// Analytics
import {
  CTA_CALL_CLICK,
  CTA_SEND_CLICK,
  CTA_WHATSAPP_CLICK,
  FILE_MANAGER_EVENT,
  ITEM_CLICKED,
  trackEvent,
} from 'src/analytics';
import useNotification, {
  ClientNotificationModel,
} from '../../hooks/useNotification';

const useStyles = makeStyles(() =>
  createStyles({
    contactToolbar: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: '#F5FAFF',
      border: '1px solid #EAEAEA',
      boxSizing: 'border-box',
      borderRadius: '0px 0px 10px 10px',
      color: colors.madison,
      minHeight: 80,
    },
    buttonContainer: {
      display: 'flex',
      flexFlow: 'column',
      color: colors.madison,
      '&:hover': {
        color: colors.bright_turquoise,
      },
    },
    disabledButton: {
      display: 'flex',
      flexFlow: 'column',
      color: colors.silver,
    },
    notificationToastButton: {
      color: colors.bright_turquoise,
    },
    notificationSnackBar: {
      backgroundColor: '#0D2041',
      color: colors.white,
      fontWeight: 'lighter',
      borderRadius: 10,
      width: 346,
      height: 65,
    },
  }),
);

const makeNotificationRequest = (
  quotation: Quotation,
): ClientNotificationModel => {
  const email =
    quotation.customerType === 'N'
      ? quotation.customer.email
      : quotation.legalEntity?.email;

  return {
    messageType: 'client-quotation-notification',
    messageParams: {
      quotationId: quotation.id,
      emailClient: email === undefined ? '' : email,
      emailCreatorUser: getLoggedEmail(),
      clientName: quotation.customer.firstName,
      inspectionRequired: !quotation.units[0].unit.isNewCar,
      quotationUrlSalt: quotation.urlSalt,
      isAseUser: getLoggedRole() === 'ASE',
    },
  };
};

const reportClickEvent = (element: string) => {
  trackEvent({
    eventName: FILE_MANAGER_EVENT,
    eventProperties: {
      [ITEM_CLICKED]: element,
    },
  });
};

const ContactClient = ({ quotation, showNotification }: Props): JSX.Element => {
  const classes = useStyles();
  const [open, toggleOpen] = useState(false);
  const [sendedEmail, setSendEmail] = useState(false);

  const { fetchStatus, sendNotification } = useNotification();
  const phoneNumber =
    quotation.customerType === 'N'
      ? quotation.customer.phone
      : quotation.legalEntity?.phone;

  const handleSendEmail = useCallback(
    (sendEmail: boolean) => {
      if (sendEmail) {
        sendNotification(makeNotificationRequest(quotation));
      }
      setSendEmail(sendEmail);
      toggleOpen(false);
    },
    [quotation, sendNotification],
  );

  useEffect(() => {
    if (fetchStatus === 'success') {
      showNotification(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchStatus]);

  const handleChatClick = () => {
    reportClickEvent(CTA_WHATSAPP_CLICK);
    window.open(`https://wa.me/506${phoneNumber}`);
  };

  const handleCallClick = () => {
    reportClickEvent(CTA_CALL_CLICK);
    window.open(`tel:${phoneNumber}`);
  };

  const handleSendClick = () => {
    reportClickEvent(CTA_SEND_CLICK);
    toggleOpen(true);
  };

  return (
    <>
      <div className={classes.contactToolbar}>
        <Grid container>
          <Grid item xs={12} container justifyContent="center">
            <ButtonBase
              className={classes.buttonContainer}
              aria-label="Chatear"
              id="sales-web-whatsapp-button"
              data-testid="whatsapp-button"
              component="span"
              onClick={handleChatClick}
            >
              <WhatsAppIcon fontSize="large" />
              CHATEAR
            </ButtonBase>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} container justifyContent="center">
            <ButtonBase
              className={classes.buttonContainer}
              aria-label="Llamar"
              data-testid="call-button"
              id="sales-web-call-button"
              component="span"
              onClick={handleCallClick}
            >
              <PhoneInTalkOutlinedIcon fontSize="large" />
              LLAMAR
            </ButtonBase>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12} container justifyContent="center">
            <ButtonBase
              className={
                sendedEmail || quotation.createdBy.includes('SOAT_')
                  ? classes.disabledButton
                  : classes.buttonContainer
              }
              aria-label="Enviar correo"
              component="span"
              data-testid="email-button"
              id="sales-web-email-button"
              onClick={handleSendClick}
              disabled={sendedEmail || quotation.createdBy.includes('SOAT_')}
            >
              <MailOutlinedIcon fontSize="large" />
              ENVIAR
            </ButtonBase>
          </Grid>
        </Grid>
      </div>
      <ConfirmationDialog
        open={open}
        data-testid="confirmation-dialog"
        message="¿Deseas enviar esta cotización por correo al cliente?"
        confirmAction={() => handleSendEmail(true)}
        rejectAction={() => handleSendEmail(false)}
      />
    </>
  );
};

interface Props {
  quotation: Quotation;
  showNotification: (isOpen: boolean) => void;
}

export default ContactClient;
